import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import { BlokProps } from "@/helpers/StoryblokHelpers"
import { InfoCardAndPriceSectionStoryblok, ServiceTypeStoryblok } from "@/types/component-types"
import { cn } from "@/utils/utils"
import { numericPrice } from "@/utils/vaccineLabels"
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from "@storyblok/react/rsc"

type InfoCardAndPriceSectionProps = BlokProps<InfoCardAndPriceSectionStoryblok> & {
  service: ServiceTypeStoryblok
  relatedServices?: ServiceTypeStoryblok[]
}

const labelBase = "text-[15px] font-semibold leading-[22px] tracking-[-0.045em]"
const labelClasses = `${labelBase} lg:text-[20px] lg:leading-[27px]`

const Price = ({ value }: { value: string | undefined }) => {
  const price = numericPrice(value)
  if (price === null) return <>{value?.trim() ? `${value} kr` : "Gratis"}</>
  if (price <= 0) return <>Gratis</>
  return <>{`${value} kr`}</>
}

export const InfoCardAndPriceSection = ({ blok, service, relatedServices }: InfoCardAndPriceSectionProps) => {
  const { content, price_title } = blok

  const rows = [service, ...(relatedServices ?? [])].filter(Boolean)

  const showChildColumn = rows.some(item => !item.adult_only)

  const showMemberPrice = rows.some(item => !!item.price_member)

  if (rows.length === 0) return null

  return (
    <section
      {...storyblokEditable(blok as SbBlokData)}
      className="mx-auto mb-4 mt-5 flex w-full max-w-[1155px] flex-col gap-8 px-4 lg:mt-[17px] lg:gap-10 lg:px-8"
    >
      <CardBasic
        variant="softElevated"
        className="min-h-0 gap-2 p-4 leading-[14px] lg:p-[34px] [&_h2]:mb-4 [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:leading-[22px] [&_h2]:tracking-[-0.045em] lg:[&_h2]:text-[20px] lg:[&_h2]:font-semibold lg:[&_h2]:leading-[27px] [&_p]:text-sm [&_p]:leading-5 [&_p]:tracking-normal lg:[&_p]:font-normal"
      >
        {content?.map(nested => <StoryblokServerComponent key={nested._uid} blok={nested} />)}
      </CardBasic>
      <CardBasic
        variant="default"
        className="min-h-0 flex-none gap-2 p-4 leading-[14px] lg:p-[34px] [&_h2]:mb-4 [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:leading-[22px] [&_h2]:tracking-[-0.045em] lg:[&_h2]:text-[20px] lg:[&_h2]:font-semibold lg:[&_h2]:leading-[27px] [&_p]:leading-5 [&_p]:tracking-normal lg:[&_p]:font-normal"
      >
        {/* Mobile */}
        <div className="lg:hidden">
          <div className="mb-4 text-neutral-50">
            <p className={cn(labelBase, "text-neutral-90")}>{price_title || "Pris"}</p>
          </div>

          {rows.map((row, index) => (
            <div key={row._uid ?? index} className={index > 0 ? "mt-8 border-t border-[#E7E7E7] pt-6" : undefined}>
              <p className={cn(labelBase, "mt-3 text-neutral-50")}>{row.name}</p>
              {row.info && <p className={cn(labelBase, "text-[14px] font-normal text-neutral-50")}>{row.info}</p>}

              <dl
                className={cn(
                  "mt-4 flex flex-col [&>div:last-child]:border-0 [&>div]:flex [&>div]:items-baseline [&>div]:justify-between [&>div]:gap-4 [&>div]:border-b [&>div]:border-[#E7E7E7] [&>div]:py-3",
                  labelBase,
                  "text-neutral-50"
                )}
              >
                <div>
                  <dt>Vuxen</dt>
                  <dd className="whitespace-nowrap text-neutral-90">
                    <Price value={row.price_adult} />
                  </dd>
                </div>
                {showChildColumn && (
                  <div>
                    <dt>Barn</dt>
                    <dd className="whitespace-nowrap text-neutral-90">
                      {row.adult_only ? "—" : <Price value={row.price_child} />}
                    </dd>
                  </div>
                )}
                {showMemberPrice && (
                  <div>
                    <dt>Apoteket AB medlem</dt>
                    <dd className="whitespace-nowrap text-neutral-90">
                      {row.price_member ? <Price value={row.price_member} /> : "—"}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          ))}
        </div>
        {/* Not mobile */}
        <table className="hidden w-full table-fixed lg:table">
          <thead>
            <tr
              className={cn(
                "[&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-[#E7E7E7] [&_th]:pb-6",
                labelClasses
              )}
            >
              <th className="text-left text-neutral-90">{price_title || "Pris"}</th>
              <th className="w-[22%] text-right text-neutral-50">Vuxen</th>
              {showChildColumn && <th className="w-[22%] text-right text-neutral-50">Barn</th>}
              {showMemberPrice && <th className="w-[22%] text-right text-neutral-50">Apoteket AB medlem</th>}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row._uid ?? index}
                className={cn(
                  "[&:last-child_td]:border-0 [&_td]:border-b [&_td]:border-[#E7E7E7] [&_td]:py-6 [&_td]:align-top [&_td]:text-neutral-50",
                  labelClasses
                )}
              >
                <td className="pr-6 text-neutral-90">
                  {row.name}

                  {service?.info && (
                    <div className="info">
                      <p className={cn(labelBase, "text-neutral-50")}>{service.info}</p>
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap text-right tabular-nums">
                  <Price value={row.price_adult} />
                </td>
                {/* A dash, not an empty cell: "not applicable" is the fact, and a blank reads as a
                    price nobody filled in. */}
                {showChildColumn && (
                  <td className="whitespace-nowrap text-right tabular-nums">
                    {row.adult_only ? "—" : <Price value={row.price_child} />}
                  </td>
                )}
                {showMemberPrice && (
                  <td className="whitespace-nowrap text-right tabular-nums">
                    {row.price_member ? <Price value={row.price_member} /> : "—"}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </CardBasic>
    </section>
  )
}
